/**
 * BisimulationExplainer — progressive-disclosure explainer for bisimulation.
 *
 * Bisimulation is the canonical "behavioral equivalence" induced by an
 * F-coalgebra: two states are bisimilar iff every observation step keeps
 * them related. CSK uses bisimulation to certify INV.PRESERVE and to give
 * noninterference, opacity, and refactoring soundness a single statement.
 *
 * The relevance tier hosts a small interactive — `LockstepStepper` — that
 * lets the reader advance two implementations together and watch the
 * observation trace accumulate on both sides as identical chips. A toggle
 * switches in a "side-channel" implementation whose trace diverges, so the
 * reader can directly see how bisimulation breaks when an attacker gains
 * an extra observation.
 *
 * Acceptance criteria (issue #1551 — bisimulation side):
 *   - Bisimulation diagram with step-buttons     ✓ `LockstepStepper`
 *   - INV.PRESERVE framing                       ✓ relevance tier
 *   - Behavioral-equivalence theorem             ✓ formal tier
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useMemo, useState } from "react";
import { FONT, GRAY, THEOREM_COLOR, withAlpha } from "../../design-system/tokens.js";
import { MathBlock, MathInline, tex } from "../../components/math/index.js";
import {
  BisimulationDiagram,
  CalloutBox,
  ExplainerCard,
  WorkedExample,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";

const ACCENT = THEOREM_COLOR["INV.PRESERVE"];

const IDENTITY: ExplainerIdentity = {
  id: "explainer-bisimulation",
  kind: "Categorical structure",
  title: "Bisimulation",
  subtitle: "R ⊆ S × S with (s₁, s₂) ∈ R ⇒ (γ s₁, γ s₂) ∈ F(R)",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "F-Coalgebra", href: "#explainer-fcoalgebra", accent: ACCENT },
  { label: "INV.PRESERVE (I2)", href: "#explainer-inv-preserve", accent: ACCENT },
  { label: "INV.CONSISTENCY", href: "#explainer-inv-consistency" },
];

// ---------------------------------------------------------------------------
// Lockstep stepper — interactive bisimulation visualization
// ---------------------------------------------------------------------------

/**
 * A toy authentication scenario. Two implementations are advertised as
 * doing the same thing:
 *
 *   - Left: bcrypt-based password verification
 *   - Right: argon2-based password verification
 *
 * Their observable trace (the chips below) is the sequence of HTTP-level
 * events an attacker can see. When the trace is bisimilar, an attacker
 * cannot tell which implementation is in the box. When a side channel
 * leaks the algorithm (the "side-channel" mode), the right side emits an
 * extra observation step and the bisimulation breaks.
 */
interface TraceStep {
  readonly leftState: string;
  readonly rightState: string;
  readonly observation: string;
  /** Right-only extra emission added by the side-channel implementation. */
  readonly sideChannelObservation?: string;
}

const BISIM_TRACE: readonly TraceStep[] = [
  {
    leftState: "AwaitInput",
    rightState: "AwaitInput",
    observation: "POST /login",
  },
  {
    leftState: "Hashing(bcrypt)",
    rightState: "Hashing(argon2)",
    observation: "200 OK",
  },
  {
    leftState: "Validated",
    rightState: "Validated",
    observation: "Set-Cookie",
  },
  {
    leftState: "Session",
    rightState: "Session",
    observation: "GET /home",
    // The side-channel implementation emits an extra "X-Algo: argon2" header.
    sideChannelObservation: "X-Algo: argon2",
  },
] as const;

type Mode = "fair" | "sideChannel";

function LockstepStepper(): React.JSX.Element | null {
  const [mode, setMode] = useState<Mode>("fair");
  const [step, setStep] = useState<number>(0);

  const totalSteps = BISIM_TRACE.length;
  const visible = useMemo(() => BISIM_TRACE.slice(0, step + 1), [step]);

  const advance = useCallback(() => {
    setStep((s) => Math.min(s + 1, totalSteps - 1));
  }, [totalSteps]);
  const reset = useCallback(() => {
    setStep(0);
  }, []);
  const switchMode = useCallback((m: Mode) => {
    setMode(m);
    setStep(0);
  }, []);

  // Bisimulation holds iff no step's `sideChannelObservation` has been
  // reached in the side-channel mode. In fair mode, the trace is by
  // construction bisimilar at every step.
  const breakIndex = useMemo(
    () => BISIM_TRACE.findIndex((t) => t.sideChannelObservation !== undefined),
    [],
  );

  // `step` is clamped to [0, totalSteps - 1] via `advance` / `reset` /
  // `switchMode`. The guard exists so the linter does not lean on a
  // non-null assertion, and so an empty BISIM_TRACE could not produce a
  // runtime crash if someone ever truncates the constant.
  const current = BISIM_TRACE[step];
  if (current === undefined) return null;

  const bisimBroken = mode === "sideChannel" && step >= breakIndex;
  const accentNow = bisimBroken ? "#C02323" : ACCENT;

  return (
    // <section> gives the demo a landmark role so screen readers reliably
    // expose its aria-label; the live-region inside (the verdict <p>) then
    // announces verdict changes as the reader steps or toggles modes.
    <section
      style={{
        marginTop: "0.75rem",
        padding: "0.875rem",
        backgroundColor: withAlpha(accentNow, "0D"),
        borderRadius: "0.5rem",
        border: `1px solid ${withAlpha(accentNow, "44")}`,
      }}
      aria-label="Bisimulation stepper demo"
    >
      {/* Mode toggle */}
      <div
        role="group"
        aria-label="Bisimulation mode"
        style={{
          display: "flex",
          gap: "0.5rem",
          marginBottom: "0.625rem",
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        <ModeButton
          label="Fair (bcrypt vs argon2)"
          active={mode === "fair"}
          accent={ACCENT}
          onClick={() => {
            switchMode("fair");
          }}
        />
        <ModeButton
          label="Side-channel leak"
          active={mode === "sideChannel"}
          accent="#C02323"
          onClick={() => {
            switchMode("sideChannel");
          }}
        />
      </div>

      {/* Per-side state + observation chip strip */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
          gap: "0.5rem",
          fontFamily: FONT.mono,
          fontSize: "0.8125rem",
        }}
      >
        <div style={{ textAlign: "right" }}>
          <div style={{ color: GRAY[500], fontSize: "0.6875rem", textTransform: "uppercase" }}>
            Implementation L
          </div>
          <div style={{ color: GRAY[900], fontWeight: 600 }}>{current.leftState}</div>
        </div>
        <div
          style={{
            color: accentNow,
            fontWeight: 700,
            padding: "0.25rem 0.75rem",
            textAlign: "center",
            fontFamily: FONT.body,
            fontSize: "0.6875rem",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          step {step + 1} of {totalSteps}
        </div>
        <div style={{ textAlign: "left" }}>
          <div style={{ color: GRAY[500], fontSize: "0.6875rem", textTransform: "uppercase" }}>
            Implementation R
          </div>
          <div style={{ color: GRAY[900], fontWeight: 600 }}>{current.rightState}</div>
        </div>
      </div>

      {/* Observation trace chips: identical across L and R when bisimilar. */}
      <div
        style={{
          marginTop: "0.625rem",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "0.5rem",
        }}
        aria-label="Observation traces"
      >
        <ObservationTrace
          who="L"
          accent={ACCENT}
          observations={visible.map((s) => s.observation)}
        />
        <ObservationTrace
          who="R"
          accent={bisimBroken ? "#C02323" : ACCENT}
          observations={visible.flatMap((s) =>
            mode === "sideChannel" && s.sideChannelObservation !== undefined
              ? [s.observation, s.sideChannelObservation]
              : [s.observation],
          )}
        />
      </div>

      <p
        role="status"
        aria-live="polite"
        style={{
          margin: "0.625rem 0 0",
          fontSize: "0.8125rem",
          color: GRAY[700],
          textAlign: "center",
        }}
      >
        {bisimBroken ? (
          <span style={{ color: "#C02323", fontWeight: 600 }}>
            R emitted <code>{current.sideChannelObservation}</code>. The traces diverge — an
            attacker can now tell L from R. The relation <MathInline>{tex`R`}</MathInline> is no
            longer a bisimulation.
          </span>
        ) : mode === "sideChannel" && step < breakIndex ? (
          <>Both sides agree so far — the side channel has not fired yet.</>
        ) : (
          <>
            Both sides agree at every step; an outside observer cannot tell L from R. The relation{" "}
            <MathInline>{tex`R`}</MathInline> is a bisimulation.
          </>
        )}
      </p>

      {/* Controls */}
      <div
        style={{
          display: "flex",
          gap: "0.5rem",
          marginTop: "0.625rem",
          justifyContent: "center",
        }}
      >
        <button
          type="button"
          onClick={advance}
          disabled={step >= totalSteps - 1}
          style={{
            padding: "0.3rem 0.75rem",
            fontSize: "0.8125rem",
            backgroundColor: step >= totalSteps - 1 ? GRAY[300] : accentNow,
            color: step >= totalSteps - 1 ? GRAY[600] : "white",
            border: "none",
            borderRadius: "0.25rem",
            cursor: step >= totalSteps - 1 ? "not-allowed" : "pointer",
            fontWeight: 600,
            fontFamily: FONT.body,
          }}
        >
          Step both sides
        </button>
        <button
          type="button"
          onClick={reset}
          style={{
            padding: "0.3rem 0.75rem",
            fontSize: "0.8125rem",
            backgroundColor: "transparent",
            color: GRAY[700],
            border: `1px solid ${GRAY[300]}`,
            borderRadius: "0.25rem",
            cursor: "pointer",
            fontFamily: FONT.body,
          }}
        >
          Reset
        </button>
      </div>
    </section>
  );
}

function ModeButton({
  label,
  active,
  accent,
  onClick,
}: {
  readonly label: string;
  readonly active: boolean;
  readonly accent: string;
  readonly onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        appearance: "none",
        border: active ? `2px solid ${accent}` : `1px solid ${GRAY[300]}`,
        background: active ? withAlpha(accent, "1A") : "white",
        borderRadius: "0.35rem",
        padding: "0.25rem 0.6rem",
        cursor: "pointer",
        fontSize: "0.75rem",
        fontWeight: 600,
        color: active ? accent : GRAY[700],
        fontFamily: FONT.body,
      }}
    >
      {label}
    </button>
  );
}

function ObservationTrace({
  who,
  accent,
  observations,
}: {
  readonly who: "L" | "R";
  readonly accent: string;
  readonly observations: readonly string[];
}): React.JSX.Element {
  return (
    <div
      style={{
        background: "white",
        border: `1px solid ${withAlpha(accent, "44")}`,
        borderRadius: "0.35rem",
        padding: "0.4rem 0.5rem",
        minHeight: "3.25rem",
      }}
    >
      <div
        style={{
          fontSize: "0.6875rem",
          color: GRAY[500],
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          marginBottom: "0.25rem",
          fontWeight: 700,
        }}
      >
        {who} observation trace
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem" }}>
        {observations.length === 0 && (
          <span style={{ fontSize: "0.75rem", color: GRAY[500] }}>(empty)</span>
        )}
        {observations.map((obs, i) => (
          <span
            key={`${who}-${String(i)}-${obs}`}
            style={{
              padding: "0.1rem 0.4rem",
              fontFamily: FONT.mono,
              fontSize: "0.7rem",
              borderRadius: "9999px",
              background: withAlpha(accent, "1A"),
              color: accent,
              fontWeight: 600,
            }}
          >
            {obs}
          </span>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tiers
// ---------------------------------------------------------------------------

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      Two states are <strong>bisimilar</strong> if no sequence of observations can tell them apart.
      CSK uses bisimulation to give "indistinguishable from the outside" a precise structural
      meaning — and to certify that refactoring an implementation cannot leak new information to an
      attacker.
    </p>
  ),
  intuition: (
    <>
      <p>
        Picture two implementations of the same login service. Internally they are very different —
        one hashes with bcrypt, the other with argon2 — but every HTTP request and response sequence
        is identical. From the outside they are the same service. <em>That</em> is bisimulation: a
        relation that survives one observation step on both sides.
      </p>
      <p>
        Now imagine the argon2 implementation accidentally emits a debug header{" "}
        <code>X-Algo: argon2</code>. With one extra observation, an attacker can tell which
        implementation is in the box, target only argon2-specific weaknesses, and pivot from there.
        The bisimulation has broken — and so has the privacy guarantee.
      </p>
      <CalloutBox eyebrow="The one-line takeaway" accent={ACCENT}>
        <em>Bisimulation is "agrees on everything the attacker can see, forever."</em> When CSK
        proves two systems bisimilar, it has proved that no attacker observation can distinguish
        them — by construction, not by audit.
      </CalloutBox>
    </>
  ),
  formal: (
    <>
      <p>
        Given an F-coalgebra <MathInline>{tex`(S, \gamma : S \to F(S))`}</MathInline>, a{" "}
        <strong>bisimulation</strong> is a relation{" "}
        <MathInline>{tex`R \subseteq S \times S`}</MathInline> equipped with a coalgebra structure{" "}
        <MathInline>{tex`(R, \gamma_{R} : R \to F(R))`}</MathInline> such that the projections{" "}
        <MathInline>{tex`\pi_{1}, \pi_{2} : R \to S`}</MathInline> are coalgebra morphisms.
        Equivalently:
      </p>
      <BisimulationDiagram accent={ACCENT} />
      <p style={{ marginTop: "0.5rem" }}>
        For every <MathInline>{tex`(s_{1}, s_{2}) \in R`}</MathInline> there is a witness in{" "}
        <MathInline>{tex`F(R)`}</MathInline> projecting to{" "}
        <MathInline>{tex`(\gamma\, s_{1}, \gamma\, s_{2})`}</MathInline> in{" "}
        <MathInline>{tex`F(S) \times F(S)`}</MathInline>. Concretely: take one step on each side and
        the observations are still related.
      </p>
      <p style={{ marginTop: "0.75rem" }}>
        <strong>Greatest bisimulation and coinduction.</strong> The class of all bisimulations on{" "}
        <MathInline>{tex`S`}</MathInline> is closed under arbitrary union (the union of a family of
        bisimulations is again a bisimulation). Therefore a <em>greatest</em> bisimulation exists —
        it is <strong>behavioral equivalence</strong> <MathInline>{tex`\sim`}</MathInline>. The{" "}
        <strong>coinduction principle</strong> is its proof rule:{" "}
        <em>
          to prove <MathInline>{tex`s_{1} \sim s_{2}`}</MathInline>, it suffices to exhibit any
          bisimulation on <MathInline>{tex`S`}</MathInline> that contains{" "}
          <MathInline>{tex`(s_{1}, s_{2})`}</MathInline>
        </em>
        . This is the exact dual of the induction principle on <MathInline>{tex`\mu F`}</MathInline>{" "}
        ("to prove P(x) for all x, show P holds for every F-constructor").
      </p>
      <p style={{ marginTop: "0.5rem" }}>
        The canonical-bisimulation theorem states that two states{" "}
        <MathInline>{tex`s_{1}, s_{2}`}</MathInline> are behaviorally equivalent iff their images in
        the terminal coalgebra <MathInline>{tex`\nu F`}</MathInline> agree:
      </p>
      <MathBlock ariaLabel="s_1 ~ s_2 iff ana γ (s_1) equals ana γ (s_2)">
        {tex`s_{1} \sim s_{2} \;\Leftrightarrow\; \mathrm{ana}\, \gamma\, (s_{1}) = \mathrm{ana}\, \gamma\, (s_{2})`}
      </MathBlock>
      <p style={{ marginTop: "0.625rem" }}>
        This is the workhorse: instead of checking that the two states agree on every possible
        observation sequence (an infinitary argument), it is enough to supply a single bisimulation
        relation — a finite certificate. That certificate is what a proof assistant produces, and
        what CSK mechanizes as the INV.PRESERVE witness.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        Bisimulation is how CSK certifies <strong>INV.PRESERVE</strong>: the invariant relation{" "}
        <MathInline>{tex`\mathrm{Valid} \subseteq S \times S`}</MathInline> is itself a
        bisimulation, so two valid states stay valid under every observation step — and stay
        observationally equivalent up to that invariant.
      </p>
      <WorkedExample
        eyebrow="Real-world story"
        title="Two hash implementations that an attacker cannot distinguish"
        accent={ACCENT}
        scenario={
          <>
            A login service can be implemented with bcrypt or argon2. Both produce the same
            HTTP-visible behavior: <code>POST /login</code> → <code>200 OK</code> →{" "}
            <code>Set-Cookie</code> → <code>GET /home</code>. The two state spaces differ
            internally, but the relation "L and R are at the same pipeline stage" is a bisimulation:
            every coalgebra step keeps them paired.
          </>
        }
        withoutStructure={
          <>
            Without a bisimulation argument, swapping the hash backend is a manual audit task:
            re-test every endpoint, hope no timing or header difference slips through. A
            <code> X-Algo</code> debug header added by a junior developer breaks unconditional user
            privacy and nobody notices until an exploit drops.
          </>
        }
        withStructure={
          <>
            Once the bisimulation is mechanized, any change to the coalgebra <em>must</em> preserve
            it or the type-checker refuses the build. The "leaky debug header" is not stripped by a
            runtime filter — there is no closed term whose anamorphism produces it on one side and
            not the other. The side channel is <em>structurally inexpressible</em>.
          </>
        }
      />
      <p style={{ marginTop: "0.75rem" }}>
        Try the stepper below: in "fair" mode both sides advance with identical observations and the
        relation holds. Switch to "side-channel leak" mode and watch the right side emit one extra
        chip on step 4 — the bisimulation breaks and an attacker can now distinguish L from R.
      </p>
      <LockstepStepper />
      <p style={{ marginTop: "0.625rem", fontSize: "0.8125rem", color: GRAY[600] }}>
        The same machinery gives noninterference, opacity, anonymity, and strong-secrecy a single
        statement (see <code>theory/type-theory/Monads-And-Algebras.md</code> §9). Two governance
        implementations that share a bisimulation neutralize the same vulnerability classes by
        construction.
      </p>
    </>
  ),
  references: [
    {
      label: "Monads-And-Algebras (§9 Bisimulation: Observational Equivalence)",
      path: "theory/type-theory/Monads-And-Algebras.md",
      note: "Bisimulation, behavioral equivalence, and the terminal-coalgebra characterization.",
    },
    {
      label: "ReactiveBisimulation (Agda)",
      path: "agda/Research/ReactiveAgentSafety/ReactiveBisimulation.agda",
      note: "Core bisimulation definition for reactive systems.",
    },
    {
      label: "IntegralBisimulation (Agda)",
      path: "agda/Research/IntegralGradedCoalgebraicSecurity/IntegralBisimulation.agda",
      note: "Integral graded coalgebra bisimulation.",
    },
    {
      label: "INV.PRESERVE derived predicates (Agda)",
      path: "agda/Kernel/INV/PRESERVE/DerivedPredicates.agda",
      note: "Invariant preservation expressed via bisimulation.",
    },
    {
      label: "Container bisimulation (Agda)",
      path: "agda/MathLib/Container/ContainerBisimulation.agda",
      note: "Container-flavored bisimulation used by streams and lenses.",
    },
    {
      label: "Structural Invariant Safety Guarantees manuscript (PR #1544)",
      path: "https://github.com/InvariantHoldings/categorical-safety-kernel/pull/1544",
      note: "Chapter 03 uses bisimulation as the INV.PRESERVE witness.",
    },
  ],
};

export function BisimulationExplainer(): React.JSX.Element {
  return (
    <ExplainerCard
      identity={IDENTITY}
      tiers={TIERS}
      diagram={<BisimulationDiagram accent={ACCENT} />}
      crossLinks={CROSS_LINKS}
    />
  );
}
